import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  buildSharedSessionsQueryKey,
  targetFromMutationRequest,
} from "./shared-session-cache-utils";
import type { SharedSession } from "./types";

import { API_URL } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { displayErrorToast } from "@/utils/toast";

export interface UpdateSharedSessionRequest {
  workspace_id: number;
  prompt_id?: number;
  workflow_id?: number;
  skill_collection_id?: string;
  shared_session_id: string;
  remix_enabled?: boolean;
  is_active?: boolean;
  batch_enabled?: boolean;
  chat_enabled?: boolean;
  is_public?: boolean;
  name?: string;
  label_name?: string | null;
  version_number?: number | null;
}

export const useUpdateSharedSession = () => {
  const authContext = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    // ── Optimistic update ────────────────────────────────────────────────────
    // Merge the incoming fields into the matching session in the list cache
    // before the request even leaves the browser, then save the previous data
    // so we can roll back if the server rejects it.
    onMutate: async ({
      workspace_id,
      prompt_id,
      workflow_id,
      skill_collection_id,
      shared_session_id,
      ...fields
    }) => {
      const sessionsKey = buildSharedSessionsQueryKey(
        targetFromMutationRequest({
          workspace_id,
          prompt_id,
          workflow_id,
          skill_collection_id,
        }),
      );

      // Cancel any in-flight refetches so they don't overwrite our optimistic write.
      await queryClient.cancelQueries({ queryKey: sessionsKey });

      // Snapshot the current value for rollback.
      const previousSessions = queryClient.getQueryData<SharedSession[]>(sessionsKey);

      queryClient.setQueryData<SharedSession[]>(sessionsKey, old =>
        old ? old.map(s => (s.id === shared_session_id ? { ...s, ...fields } : s)) : old,
      );

      return { previousSessions, sessionsKey };
    },

    mutationFn: async ({
      workspace_id,
      shared_session_id,

      prompt_id: _prompt_id,
      workflow_id: _workflow_id,
      skill_collection_id: _skill_collection_id,
      ...body
    }: UpdateSharedSessionRequest) => {
      const response = await fetch(
        `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/shared_sessions/${shared_session_id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authContext?.userToken}`,
          },
          credentials: "include",
          body: JSON.stringify(body),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update shared session");
      }

      return response.json() as Promise<{ success: boolean; shared_session: SharedSession }>;
    },

    // ── Confirmed by server — replace optimistic write with real data ────────
    onSuccess: (data, { workspace_id, prompt_id, workflow_id, skill_collection_id }) => {
      const updatedSession = data.shared_session;
      const sessionsKey = buildSharedSessionsQueryKey(
        targetFromMutationRequest({
          workspace_id,
          prompt_id,
          workflow_id,
          skill_collection_id,
        }),
      );

      queryClient.setQueryData<SharedSession[]>(sessionsKey, old =>
        old ? old.map(s => (s.id === updatedSession.id ? updatedSession : s)) : old,
      );
    },

    // ── Rollback on error ────────────────────────────────────────────────────
    onError: (error: Error, _variables, context) => {
      if (context?.sessionsKey && context.previousSessions !== undefined) {
        queryClient.setQueryData(context.sessionsKey, context.previousSessions);
      }
      displayErrorToast(error.message || "Failed to update shared session");
    },
  });
};
